import classes from './ChatWidget.module.scss'
import { IconBot, IconBox, IconChevron, IconGift, IconStar, IconTag, IconWallet } from './icons'

/**
 * Bir temaya tıklandığında ne olacağı. `ask` doğrudan serbest metin sorgusu atar; `prompt`
 * parametre isteyen alanlar için (ör. ürün arama) mevcut `pending` akışını açar — kod/ad olmadan
 * anlamlı bir sorgu kurulamayacağı için tıklama sahte bir yanıt yerine soruyu sorar.
 */
export type IntroThemeAction =
  | { kind: 'ask'; question: string }
  | { kind: 'prompt'; intent: string; paramKey: string; label: string; message: string }

interface Props {
  /** Tema tıklandı → eylemi widget yürütür (kart backend'e hiç dokunmaz). */
  onTheme: (action: IntroThemeAction) => void
  /** "Tüm yetenekleri gör" → sağ Hızlı Bağlantılar sidebar'ını açar. */
  onShowAll: () => void
  disabled?: boolean
}

/**
 * Bir hesabın gördüğü ilk yetenek temaları. Bilinçli olarak elle yazılı: buton kataloğunun
 * (`ChatButtonsApi`) grup adları teknik ve sayısı değişken, kartın anlatısı kontrollü kalmalı.
 * Yeni bir yetenek ailesi eklendiğinde bu liste elle güncellenir.
 */
export const INTRO_THEMES: {
  Icon: () => JSX.Element
  label: string
  hint: string
  action: IntroThemeAction
}[] = [
  {
    Icon: IconBox,
    label: 'Sipariş ve kargo takibi',
    hint: 'sipariş listesi, detay, kargo, seri no',
    action: { kind: 'ask', question: 'Son siparişlerim?' },
  },
  {
    Icon: IconTag,
    label: 'Ürün, stok ve bayi fiyatı',
    hint: 'arama, stok sorgu, marka/kategori katalog',
    action: {
      kind: 'prompt',
      intent: 'searchUrun',
      paramKey: 'q',
      label: 'Ürün arama',
      message: 'Hangi ürünü arayayım? Ürün kodunu ya da adını yazabilirsiniz.',
    },
  },
  {
    Icon: IconWallet,
    label: 'Cari, bakiye ve faturalar',
    hint: 'bakiye/risk, borç durumu, teminat, komisyon',
    action: { kind: 'ask', question: 'Bakiyem ne kadar?' },
  },
  {
    Icon: IconGift,
    label: 'Kampanya, kupon ve fırsatlar',
    hint: 'kuponlar, fırsat pazarı, en çok aldıklarınız',
    action: { kind: 'ask', question: 'Bu ay hangi kampanyalar var?' },
  },
]

/**
 * İlk temas tanıtım kartı: bir hesabın ilk oturumunda karşılama balonunun altında,
 * "Hızlı Bağlantılar" CTA'sının yerine görünür. Dört tema da tıklanabilir — kart bir bilgi
 * listesi değil, eylem menüsüdür.
 */
export default function IntroCard({ onTheme, onShowAll, disabled }: Props) {
  return (
    <div className={classes.introCard}>
      <div className={classes.introHead}>
        <span className={classes.introAvatar}>
          <IconBot />
          <span className={classes.introAvatarDot} aria-hidden='true' />
        </span>
        <span className={classes.introIdentity}>
          <span className={classes.introName}>B-Gini</span>
          <span className={classes.introRole}>Index Pazar dijital asistanı</span>
        </span>
      </div>

      <p className={classes.introLead}>
        Sipariş, stok, cari ve kampanya sorularınızı doğal dille sorabilirsiniz — menülerde gezmenize gerek yok.
      </p>

      <p className={classes.introThemesLabel}>Neler yapabilirim?</p>
      <div className={classes.introThemes}>
        {INTRO_THEMES.map(({ Icon, label, hint, action }, i) => (
          <button
            key={label}
            type='button'
            className={classes.introTheme}
            style={{ animationDelay: `${120 + i * 60}ms` }}
            onClick={() => onTheme(action)}
            disabled={disabled}
          >
            <span className={classes.introThemeIcon}>
              <Icon />
            </span>
            <span className={classes.introThemeText}>
              <span className={classes.introThemeLabel}>{label}</span>
              <span className={classes.introThemeHint}>{hint}</span>
            </span>
            <span className={classes.introThemeChevron}>
              <IconChevron />
            </span>
          </button>
        ))}
      </div>

      <button type='button' className={classes.introShowAll} onClick={onShowAll}>
        <span className={classes.introShowAllIcon}>
          <IconStar />
        </span>
        <span className={classes.introShowAllLabel}>Tüm yetenekleri gör</span>
        <span className={classes.introShowAllChevron}>
          <IconChevron />
        </span>
      </button>

      <p className={classes.introDisclaimer}>B-Gini yanıtları bazen hatalı olabilir.</p>
    </div>
  )
}
